import { useEffect, useState, type FormEvent } from "react";

import { Alert, Badge, Button, Card, CardTitle, Input, Label, NativeSelect, Textarea } from "@realroof/ui";

import { api, canManage } from "../api";
import { useSession } from "../session";
import { Table, td, th } from "../table";
import { CityChooser, type Place } from "./Locations";

export interface Box {
  id: string;
  listing_id: string;
  public_id: string;
  title: string;
  listing_status: string;
  city_id: string | null;
  city: string | null;
  also_city_ids: string[];
  slot: number;
  starts_on: string;
  ends_on: string;
  badge: string;
  headline: string | null;
  subtext: string | null;
  image_key: string | null;
  image: string | null;
  alt: string | null;
  invoice_no: string | null;
  invoice_amount: number | null;
  client_name: string | null;
  sales_person: string | null;
  notes: string | null;
  draft: boolean;
  paused: boolean;
  state: "draft" | "scheduled" | "live" | "paused" | "ad_not_live" | "ended";
  impressions: number;
  clicks: number;
  leads: number;
}

const STATE: Record<Box["state"], [string, "success" | "warning" | "neutral" | "danger" | "info"]> = {
  live: ["Live", "success"],
  scheduled: ["Scheduled", "info"],
  paused: ["Paused", "warning"],
  ad_not_live: ["Paused: ad not live", "danger"],
  draft: ["Draft", "neutral"],
  ended: ["Ended", "neutral"],
};
const BADGES = ["Featured", "New launch", "Offer"];
const SLOTS = [1, 2, 3, 4, 5, 6, 7, 8];
const day = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (iso: string, n: number) => new Date(new Date(`${iso}T00:00:00Z`).getTime() + n * 86_400_000).toISOString().slice(0, 10);

/** Home-page box ads (wireframe A09): 8 slots per city, sold offline, placed here with the invoice. */
export function Boxes() {
  const { me } = useSession();
  const [city, setCity] = useState<Place | null>(null);
  const [rows, setRows] = useState<Box[] | null>(null);
  const [editing, setEditing] = useState<Box | "new" | null>(null);
  const [message, setMessage] = useState<{ kind: "error" | "success"; text: string } | null>(null);
  const manage = canManage(me?.staff_role);

  const load = () =>
    api<Box[]>(`/admin/boxes${city ? `?city=${city.id}` : ""}`)
      .then(setRows)
      .catch((err: Error) => setMessage({ kind: "error", text: err.message }));
  useEffect(() => {
    load();
  }, [city]); // eslint-disable-line react-hooks/exhaustive-deps

  async function act(box: Box, path: string, method = "POST") {
    setMessage(null);
    try {
      await api(`/admin/boxes/${box.id}${path}`, { method });
      await load();
    } catch (err) {
      setMessage({ kind: "error", text: (err as Error).message });
    }
  }

  const running = (rows ?? []).filter((b) => b.state === "live" || b.state === "scheduled" || b.state === "paused" || b.state === "ad_not_live");
  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Home page box ads</h1>
        {manage && !editing && (
          <Button variant="primary" onClick={() => setEditing("new")}>
            + New box ad
          </Button>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        Sold offline: agree the price with the owner, raise the invoice outside RealRoof, then place the box here with the invoice number. It
        shows from the start date to the end date while the ad is live; the owner is reminded 7 and 2 days before the end.
      </p>
      {message && <Alert tone={message.kind}>{message.text}</Alert>}
      {editing && (
        <BoxForm
          box={editing === "new" ? null : editing}
          onDone={(text) => {
            setEditing(null);
            setMessage({ kind: "success", text });
            load();
          }}
          onCancel={() => setEditing(null)}
        />
      )}

      <CityChooser label="City" value={city} onChange={setCity} />
      {city && (
        <div className="flex flex-col gap-2">
          <strong>Slots in {city.name} today</strong>
          <ol className="m-0 grid list-none grid-cols-2 gap-2 p-0 sm:grid-cols-4 lg:grid-cols-8" aria-label={`Slots in ${city.name}`}>
            {SLOTS.map((n) => {
              const b = running.find((x) => x.slot === n && x.city_id === city.id && x.starts_on <= today() && x.ends_on >= today());
              return (
                <li key={n} className={`rounded-md border p-2 text-sm ${b ? "border-success bg-success-soft" : "border-dashed border-border"}`}>
                  {n} · {b ? b.public_id : "free"}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {rows === null && <p className="text-muted-foreground">Loading…</p>}
      {rows?.length === 0 && <p className="text-muted-foreground">No box ads{city ? ` in ${city.name}` : ""} yet.</p>}
      {rows && rows.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th className={th}>Box</th>
              <th className={th}>Shown in</th>
              <th className={th}>Slot · period</th>
              <th className={th}>Invoice · sales</th>
              <th className={th}>Shown / clicks / leads</th>
              <th className={th}>Status</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id}>
                <td className={td}>
                  <div className="flex items-center gap-2">
                    {b.image && <img src={b.image} alt="" className="h-12 w-16 rounded object-cover" />}
                    <div>
                      <b>{b.headline || b.title}</b>
                      <div className="font-mono text-xs text-muted-foreground">{b.public_id}</div>
                    </div>
                  </div>
                </td>
                <td className={td}>
                  {b.city ?? "National"}
                  {b.also_city_ids.length > 0 && <div className="text-xs text-muted-foreground">+ {b.also_city_ids.length} nearby</div>}
                </td>
                <td className={`${td} whitespace-nowrap`}>
                  {b.slot} · {day(b.starts_on)} → {day(b.ends_on)}
                </td>
                <td className={td}>
                  {b.invoice_no ?? "–"}
                  {b.invoice_amount != null && ` · ₹${b.invoice_amount.toLocaleString("en-IN")}`}
                  <div className="text-xs text-muted-foreground">{[b.client_name, b.sales_person && `sales: ${b.sales_person}`].filter(Boolean).join(" · ")}</div>
                </td>
                <td className={td}>
                  {b.impressions.toLocaleString("en-IN")} / {b.clicks.toLocaleString("en-IN")} / {b.leads}
                </td>
                <td className={td}>
                  <Badge tone={STATE[b.state][1]}>{STATE[b.state][0]}</Badge>
                </td>
                <td className={`${td} whitespace-nowrap`}>
                  {manage && (
                    <div className="flex gap-1">
                      <Button size="sm" onClick={() => setEditing(b)}>
                        Edit
                      </Button>
                      {!b.draft && b.state !== "ended" && (
                        <Button size="sm" onClick={() => act(b, b.paused ? "/resume" : "/pause")}>
                          {b.paused ? "Resume" : "Pause"}
                        </Button>
                      )}
                      {b.draft && (
                        <Button size="sm" variant="ghost" onClick={() => act(b, "", "DELETE")}>
                          Delete
                        </Button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </section>
  );
}

function BoxForm({ box, onDone, onCancel }: { box: Box | null; onDone: (message: string) => void; onCancel: () => void }) {
  const [publicId, setPublicId] = useState(box?.public_id ?? "");
  const [city, setCity] = useState<Place | null>(box?.city_id ? ({ id: box.city_id, name: box.city ?? box.city_id, state: "" } as Place) : null);
  const [national, setNational] = useState(box ? box.city_id === null : false);
  const [also, setAlso] = useState<Place[]>(box?.also_city_ids.map((id) => ({ id, name: id, state: "" }) as Place) ?? []);
  const [slot, setSlot] = useState(box?.slot ?? 1);
  const [startsOn, setStartsOn] = useState(box?.starts_on ?? today());
  const [endsOn, setEndsOn] = useState(box?.ends_on ?? plusDays(today(), 30));
  const [badge, setBadge] = useState(box?.badge ?? "Featured");
  const [headline, setHeadline] = useState(box?.headline ?? "");
  const [subtext, setSubtext] = useState(box?.subtext ?? "");
  const [imageKey, setImageKey] = useState<string | null>(box?.image_key ?? null);
  const [preview, setPreview] = useState<string | null>(box?.image ?? null);
  const [alt, setAlt] = useState(box?.alt ?? "");
  const [invoiceNo, setInvoiceNo] = useState(box?.invoice_no ?? "");
  const [amount, setAmount] = useState(box?.invoice_amount != null ? String(box.invoice_amount) : "");
  const [client, setClient] = useState(box?.client_name ?? "");
  const [sales, setSales] = useState(box?.sales_person ?? "");
  const [notes, setNotes] = useState(box?.notes ?? "");
  const [premiumToo, setPremiumToo] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  async function upload(file: File) {
    setError("");
    setUploading(true);
    try {
      const { key, upload_url } = await api<{ key: string; upload_url: string }>("/admin/boxes/image", {
        method: "POST",
        body: { content_type: file.type, size_bytes: file.size },
      });
      const put = await fetch(upload_url, { method: "PUT", headers: { "Content-Type": file.type }, body: file });
      if (!put.ok) throw new Error("The image did not upload; try again");
      setImageKey(key);
      setPreview(URL.createObjectURL(file));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
    }
  }

  async function save(e: FormEvent, draft: boolean) {
    e.preventDefault();
    setError("");
    const body = {
      public_id: publicId.trim(),
      city_id: national ? null : (city?.id ?? null),
      also_city_ids: national ? [] : also.map((p) => p.id),
      slot,
      starts_on: startsOn,
      ends_on: endsOn,
      badge,
      headline: headline || null,
      subtext: subtext || null,
      image_key: imageKey,
      alt: alt || null,
      invoice_no: invoiceNo || null,
      invoice_amount: amount ? Number(amount) : null,
      client_name: client || null,
      sales_person: sales || null,
      notes: notes || null,
      draft,
      premium_too: premiumToo,
    };
    if (!national && !city) return setError("Choose the city, or tick National");
    try {
      await api(box ? `/admin/boxes/${box.id}` : "/admin/boxes", { method: box ? "PUT" : "POST", body });
      onDone(draft ? "Draft saved." : `Box placed in slot ${slot}.`);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <Card>
      <form className="flex flex-col gap-3" onSubmit={(e) => save(e, false)} aria-label={box ? "Edit box ad" : "New box ad"}>
        <CardTitle>{box ? "Edit box ad" : "New box ad"}</CardTitle>
        <div className="grid gap-3 md:grid-cols-2">
          <Label>
            Ad to promote (ad number)
            <Input value={publicId} onChange={(e) => setPublicId(e.target.value)} placeholder="RR-S-000412" required />
          </Label>
          <Label>
            Badge
            <NativeSelect value={badge} onChange={(e) => setBadge(e.target.value)}>
              {BADGES.map((b) => (
                <option key={b}>{b}</option>
              ))}
            </NativeSelect>
          </Label>
          <Label>
            Box headline (optional)
            <Input value={headline} maxLength={80} onChange={(e) => setHeadline(e.target.value)} />
          </Label>
          <Label>
            Box sub-text (optional)
            <Input value={subtext} maxLength={120} onChange={(e) => setSubtext(e.target.value)} />
          </Label>
        </div>

        <label className="flex items-center gap-2">
          <input type="checkbox" checked={national} onChange={(e) => setNational(e.target.checked)} /> National (shown where a city has no boxes)
        </label>
        {!national && (
          <>
            <CityChooser label="Primary city" value={city} onChange={setCity} />
            <div className="flex flex-col gap-1.5">
              <CityChooser label="Also show in" value={null} onChange={(p) => p && !also.some((a) => a.id === p.id) && setAlso([...also, p])} />
              {also.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {also.map((p) => (
                    <Button key={p.id} size="sm" aria-label={`Remove ${p.name}`} onClick={() => setAlso(also.filter((a) => a.id !== p.id))}>
                      {p.name} ✕
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <div className="grid gap-3 md:grid-cols-3">
          <Label>
            Slot
            <NativeSelect value={slot} onChange={(e) => setSlot(Number(e.target.value))}>
              {SLOTS.map((n) => (
                <option key={n} value={n}>
                  Slot {n}
                </option>
              ))}
            </NativeSelect>
          </Label>
          <Label>
            Start date
            <Input type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} required />
          </Label>
          <Label>
            End date
            <Input type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} required />
          </Label>
          <Label>
            Invoice number
            <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />
          </Label>
          <Label>
            Invoice amount (₹)
            <Input type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Label>
          <Label>
            Client name
            <Input value={client} onChange={(e) => setClient(e.target.value)} />
          </Label>
          <Label>
            Sales person
            <Input value={sales} onChange={(e) => setSales(e.target.value)} />
          </Label>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <Label>
            Box image (4:3, JPG / PNG / WebP, up to 5 MB; leave empty to use the ad&apos;s cover photo)
            <Input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
          </Label>
          {uploading && <span className="text-sm text-muted-foreground">Uploading…</span>}
          {preview && <img src={preview} alt="" className="h-24 w-32 rounded object-cover" />}
        </div>
        {imageKey && (
          <Label>
            Image description (alt text)
            <Input value={alt} maxLength={160} onChange={(e) => setAlt(e.target.value)} required />
          </Label>
        )}
        <Label>
          Internal notes
          <Textarea rows={2} maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={premiumToo} onChange={(e) => setPremiumToo(e.target.checked)} /> Make the ad premium too, for the same period
        </label>
        {error && <Alert tone="error">{error}</Alert>}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="primary" disabled={uploading}>
            Save &amp; schedule
          </Button>
          <Button type="button" disabled={uploading} onClick={(e) => save(e as unknown as FormEvent, true)}>
            Save draft
          </Button>
          <Button type="button" variant="link" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
